import { memo, useCallback, useMemo } from 'react';
import gql from 'graphql-tag';

import { markErrorAsHandled, useSubscribe } from '@confluence/graphql';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';
import { Attribution, getStatusCode } from '@confluence/error-boundary';

const subscription = gql`
	subscription ContentTypesTitleSubscription($id: ID!) {
		confluence_onContentModified(id: $id) {
			type
			id
			__typename
			_deltas
			contentTitleUpdated {
				contentTitle
				__typename
			}
		}
	}
`;

export const ContentTypesTitleSubscription = memo(
	({
		contentType,
		contentId,
		onDataReceived,
		skip = false,
	}: {
		contentType: string;
		contentId: string;
		onDataReceived: (data: string) => void;
		skip?: boolean;
	}) => {
		const canIgnoreError = useCallback(({ statusCode }: { statusCode?: number }) => {
			return statusCode === 403;
		}, []);
		const sink = useMemo(
			() => ({
				next: (value: any) => {
					const payload = value.data?.confluence_onContentModified;
					const _deltas = payload?._deltas;
					if (!payload || !Array.isArray(_deltas)) {
						return;
					}
					if (_deltas?.length === 0 || _deltas.indexOf('contentTitleUpdated') === -1) {
						return;
					}
					if (payload) {
						const { contentTitleUpdated } = payload;
						const title = contentTitleUpdated?.contentTitle;
						onDataReceived(title);
					}
				},
				error: (error: Error) => {
					const errorMessage = error?.message || (error as any)?.errors?.[0]?.message; // error object can be an array
					const statusCode =
						getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
					if (!canIgnoreError({ statusCode })) {
						const e = new Error(
							`Subscription Error - ContentTypesTitleSubscription:${errorMessage}`,
						);
						setContentErrorAttributes(e, {
							objectId: contentId,
							objectType: contentType,
						});
						getMonitoringClient().submitError(e, {
							attribution: Attribution.PAGE_EXPERIENCES,
						});
						markErrorAsHandled(error);
					}
				},
			}),
			[contentId, contentType, onDataReceived, canIgnoreError],
		);

		useSubscribe({
			query: subscription,
			operationName: 'ContentTypesTitleSubscription',
			contentId,
			contentType,
			skip,
			sink,
		});

		return null;
	},
);
